import { lineIdAt } from "@/lib/discount-evaluation/cart-lines"
import type { LoyaltyReward } from "@/lib/discount-evaluation/types"
import { refMatchesCartLine } from "@/lib/free-product-cart-item"
import { roundCurrency, sanitizeMoney } from "@/lib/pricing"
import type { Cart } from "@/types"
import type { Discount } from "@/types/external"

function rewardDiscountCode(rewardId: string): string {
  return `LOYALTY_REWARD:${rewardId}`
}

/**
 * One reward the customer chose to redeem. `claimedLineId` is the bag line
 * this checkout added for it, if any — the customer's own line is never
 * recorded, so dropping the reward never takes away what they added.
 */
export type RewardRedemption = {
  rewardId: string
  quantity: number
  claimedLineId?: string
}

/** The redeemable rewards, the points balance they draw on and what the customer picked. */
export type RewardsState = {
  balance: number
  available: LoyaltyReward[]
  redeemed: RewardRedemption[]
}

export const emptyRewardsState: RewardsState = { balance: 0, available: [], redeemed: [] }

function rewardRef(reward: LoyaltyReward) {
  return { itemId: reward.item.itemId, externalCode: reward.item.externalCode, name: reward.item.name }
}

/** Position of the bag line a reward is redeemed on: the line it added, else the first line of that item. */
export function rewardLineIndex(cart: Cart, reward: LoyaltyReward, claimedLineId?: string): number {
  if (claimedLineId) {
    const claimed = cart.items.findIndex((line) => line.id === claimedLineId)
    if (claimed >= 0) return claimed
  }
  return cart.items.findIndex((line) => refMatchesCartLine(rewardRef(reward), line))
}

function rewardById(rewards: RewardsState, rewardId: string): LoyaltyReward | undefined {
  return rewards.available.find((reward) => reward.id === rewardId)
}

/** Points the picked rewards hold, optionally leaving one reward out. */
export function reservedRewardPoints(rewards: RewardsState, exceptRewardId?: string): number {
  return rewards.redeemed.reduce((sum, redemption) => {
    if (redemption.rewardId === exceptRewardId) return sum
    const reward = rewardById(rewards, redemption.rewardId)
    return reward ? sum + reward.pointsCost * redemption.quantity : sum
  }, 0)
}

/** How many units of `reward` the balance left by the other picks still pays for, within the per-order cap. */
export function redeemableUnits(reward: LoyaltyReward, rewards: RewardsState): number {
  const left = Math.max(0, rewards.balance - reservedRewardPoints(rewards, reward.id))
  const byPoints = reward.pointsCost > 0 ? Math.floor(left / reward.pointsCost) : 0
  const cap = reward.maxPerOrder > 0 ? reward.maxPerOrder : byPoints
  return Math.max(0, Math.min(byPoints, cap))
}

/**
 * The ITEM discounts the picked rewards put on the order (C6): one per reward,
 * on the line it is redeemed on (`targetId` = lineId), worth the item's base
 * unit price — options excluded, they stay charged — times the redeemed units,
 * never more units than the line holds (contract C1.2: loyalty checks it
 * against the order item's UnitPrice). A reward whose product left the bag
 * simply drops out.
 */
export function buildRewardDiscounts(rewards: RewardsState, cart: Cart): Discount[] {
  return rewards.redeemed.flatMap((redemption) => {
    const reward = rewardById(rewards, redemption.rewardId)
    if (!reward) return []
    const index = rewardLineIndex(cart, reward, redemption.claimedLineId)
    const line = cart.items[index]
    if (!line) return []
    const units = Math.min(redemption.quantity, Math.floor(line.quantity))
    const value = roundCurrency(sanitizeMoney(line.itemOffer?.price?.value) * units)
    if (units <= 0 || value <= 0) return []
    const discountCode = rewardDiscountCode(reward.id)
    return [
      {
        amount: { value, currency: "BRL" },
        target: "ITEM",
        targetId: lineIdAt(index),
        sponsorshipValues: [{ name: "MERCHANT", amount: { value, currency: "BRL" }, discountCode }],
      },
    ]
  })
}
